import axios from 'axios'
import Qs from 'qs'
import Cookie from 'js-cookie'

const pendingGetPromises = new Map()
const responseCache = new Map()
const PERSISTENT_CACHE_PREFIX = 'nb:http-cache:'

const normalizeValue = value => {
  if (value === null || value === undefined) {
    return value
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeValue(item))
  }

  if (typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const normalized = normalizeValue(value[key])

        if (normalized !== undefined) {
          acc[key] = normalized
        }

        return acc
      }, {})
  }

  return value
}

const stableStringify = value => JSON.stringify(normalizeValue(value))

const buildRequestKey = config => {
  const method = (config?.method || 'get').toLowerCase()
  const baseURL = config?.baseURL || ''
  const url = config?.url || ''
  const params = stableStringify(config?.params || {})

  return [
    method,
    baseURL,
    url,
    params,
  ].join('|')
}

const getCacheTtl = cache => {
  if (!cache) return 0
  if (cache === true) return 5 * 60 * 1000
  if (typeof cache === 'number') return cache
  if (typeof cache?.ttl === 'number') return cache.ttl

  return 0
}

const getPersistentCacheStorage = cache => {
  if (!cache?.persist) {
    return null
  }

  if (typeof window === 'undefined') {
    return null
  }

  try {
    return cache.storage === 'sessionStorage'
      ? window.sessionStorage
      : window.localStorage
  } catch (error) {
    return null
  }
}

const getPersistentCacheKey = requestKey => {
  return `${PERSISTENT_CACHE_PREFIX}${requestKey}`
}

const createCachedResponse = cached => {
  return {
    data: cached.data,
    status: cached.status || 200,
    statusText: cached.statusText || 'OK',
    headers: cached.headers || {},
    config: cached.config || {},
    request: null,
    __fromCache: true,
  }
}

const getCachedResponse = requestKey => {
  const cached = responseCache.get(requestKey)

  if (!cached) {
    return null
  }

  if (Date.now() > cached.expiresAt) {
    responseCache.delete(requestKey)
    return null
  }

  return cached.response
}

const setCachedResponse = (requestKey, response, ttl) => {
  if (!ttl) return

  responseCache.set(requestKey, {
    response,
    expiresAt: Date.now() + ttl,
  })
}

export const clearHttpCache = matcher => {
  if (!matcher) {
    responseCache.clear()

    if (typeof window !== 'undefined') {
      try {
        Object.keys(window.localStorage)
          .filter(key => key.startsWith(PERSISTENT_CACHE_PREFIX))
          .forEach(key => window.localStorage.removeItem(key))

        Object.keys(window.sessionStorage)
          .filter(key => key.startsWith(PERSISTENT_CACHE_PREFIX))
          .forEach(key => window.sessionStorage.removeItem(key))
      } catch (error) {
        // Ignore storage cleanup errors.
      }
    }

    return
  }

  Array.from(responseCache.keys()).forEach(key => {
    if (typeof matcher === 'function' ? matcher(key) : key.includes(matcher)) {
      responseCache.delete(key)
    }
  })
}

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  paramsSerializer: function(params) {
    return Qs.stringify(params, { arrayFormat: 'brackets' })
  },
})

http.interceptors.request.use(config => {
  const csrfToken = Cookie.get('intranet_csrf_token') || null
  config.headers['X-CSRF-Token'] = csrfToken
  return config
})

http.interceptors.response.use(response => response, ({ response }) => {
  return Promise.reject(response)
})

const getPersistentCachedResponse = (requestKey, cache) => {
  const storage = getPersistentCacheStorage(cache)

  if (!storage) {
    return null
  }

  const cacheKey = getPersistentCacheKey(requestKey)

  try {
    const raw = storage.getItem(cacheKey)

    if (!raw) {
      return null
    }

    const cached = JSON.parse(raw)

    if (!cached?.expiresAt || Date.now() > cached.expiresAt) {
      storage.removeItem(cacheKey)
      return null
    }

    const response = createCachedResponse(cached)

    responseCache.set(requestKey, {
      response,
      expiresAt: cached.expiresAt,
    })

    return response
  } catch (error) {
    storage.removeItem(cacheKey)
    return null
  }
}

const setPersistentCachedResponse = (requestKey, response, ttl, cache) => {
  const storage = getPersistentCacheStorage(cache)

  if (!storage || !ttl) {
    return
  }

  try {
    storage.setItem(
      getPersistentCacheKey(requestKey),
      JSON.stringify({
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: {
          url: response.config?.url,
          method: response.config?.method,
          params: response.config?.params,
        },
        expiresAt: Date.now() + ttl,
      }),
    )
  } catch (error) {
    // Storage can fail in private mode or when quota is exceeded.
  }
}

const originalGet = http.get.bind(http)

http.get = (url, config = {}) => {
  const dedupeEnabled = config.dedupe !== false
  const cacheTtl = config.cache === false ? 0 : getCacheTtl(config.cache)

  const requestConfig = {
    ...config,
    method: 'get',
    url,
    baseURL: config.baseURL || http.defaults.baseURL,
  }

  const requestKey = buildRequestKey(requestConfig)

  if (cacheTtl) {
    const cachedResponse = getCachedResponse(requestKey)

    if (cachedResponse) {
      return Promise.resolve(cachedResponse)
    }

    const persistentCachedResponse = getPersistentCachedResponse(requestKey, config.cache)

    if (persistentCachedResponse) {
      return Promise.resolve(persistentCachedResponse)
    }
  }

  if (dedupeEnabled) {
    const existingPromise = pendingGetPromises.get(requestKey)

    if (existingPromise) {
      return existingPromise
    }
  }

  const promise = originalGet(url, config)
    .then(response => {
      setCachedResponse(requestKey, response, cacheTtl)
      setPersistentCachedResponse(requestKey, response, cacheTtl, config.cache)

      return response
    })
    .finally(() => {
      if (dedupeEnabled) {
        pendingGetPromises.delete(requestKey)
      }
    })

  if (dedupeEnabled) {
    pendingGetPromises.set(requestKey, promise)
  }

  return promise
}
